import React, { useEffect, useState, useRef } from 'react';
import { MinusIcon, PlusIcon, ShoppingBagIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useCart } from './CartContext';
export function CartDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const {
    items,
    itemCount,
    subtotal,
    updateQuantity,
    removeItem,
    isCheckingOut,
    checkoutError,
    clearCheckoutError,
    checkout
  } = useCart();
  const closeDrawer = () => {
    setIsOpen(false);
    clearCheckoutError();
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };
  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDrawer();
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled])');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
  return <>
      <button ref={triggerRef} type="button" className="cart-trigger" onClick={() => setIsOpen(true)} aria-label={`Abrir carrito${itemCount ? `, ${itemCount} productos` : ''}`} aria-haspopup="dialog" aria-expanded={isOpen} data-id="element-22">
        <ShoppingBagIcon size={19} strokeWidth={1.45} aria-hidden="true" data-id="element-23" />
        {itemCount > 0 && <span className="cart-trigger__count" data-id="element-24">{itemCount}</span>}
      </button>

      {isOpen && <div className="cart-layer" data-id="element-25">
          <button type="button" className="cart-overlay" aria-label="Cerrar carrito" onClick={closeDrawer} data-id="element-26" />
          <aside ref={drawerRef} className="cart-drawer" role="dialog" aria-modal="true" aria-labelledby="cart-title" data-id="element-27">
            <header className="cart-drawer__header" data-id="element-28">
              <div data-id="element-29">
                <p className="cart-drawer__eyebrow" data-id="element-30">SAVIA</p>
                <h2 id="cart-title" data-id="element-31">Tu carrito</h2>
              </div>
              <button ref={closeButtonRef} type="button" className="cart-icon-button" onClick={closeDrawer} aria-label="Cerrar carrito" data-id="element-32">
                <XIcon size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-33" />
              </button>
            </header>

            <div className="cart-drawer__body" data-id="element-34">
              {items.length === 0 ? <div className="cart-empty" data-id="element-35">
                  <ShoppingBagIcon size={28} strokeWidth={1.2} aria-hidden="true" data-id="element-36" />
                  <p data-id="element-37">Tu carrito está en calma.</p>
                  <span data-id="element-38">Agrega un producto para comenzar tu ritual.</span>
                </div> : <ul className="cart-items" aria-label="Productos en el carrito" data-id="element-39">
                  {items.map(item => <li className="cart-item" key={item.id} data-id="element-40">
                      {item.image ? <img src={item.image} alt="" className="cart-item__image" data-id="element-41" /> : <div className="cart-item__image cart-item__image--placeholder" aria-hidden="true" data-id="element-42" />}
                      <div className="cart-item__details" data-id="element-43">
                        <div className="cart-item__topline" data-id="element-44">
                          <h3 data-id="element-45">{item.name}</h3>
                          <button type="button" className="cart-remove" onClick={() => removeItem(item.id)} aria-label={`Eliminar ${item.name} del carrito`} data-id="element-46">
                            <Trash2Icon size={15} strokeWidth={1.45} aria-hidden="true" data-id="element-47" />
                          </button>
                        </div>
                        <p data-id="element-48">{item.aroma}</p>
                        <span data-id="element-49">{item.format}</span>
                        <span data-id="element-50">{formatCop(item.price)} c/u</span>
                        <div className="cart-item__footer" data-id="element-51">
                          <div className="cart-quantity" aria-label={`Cantidad de ${item.name}`} data-id="element-52">
                            <button type="button" onClick={() => updateQuantity(item.id, item.quantity - 1)} aria-label={`Restar una unidad de ${item.name}`} data-id="element-53">
                              <MinusIcon size={13} strokeWidth={1.5} aria-hidden="true" data-id="element-54" />
                            </button>
                            <span aria-live="polite" data-id="element-55">{item.quantity}</span>
                            <button type="button" onClick={() => updateQuantity(item.id, item.quantity + 1)} aria-label={`Sumar una unidad de ${item.name}`} data-id="element-56">
                              <PlusIcon size={13} strokeWidth={1.5} aria-hidden="true" data-id="element-57" />
                            </button>
                          </div>
                          <strong data-id="element-58">
                            {formatCop(item.price * item.quantity)}
                          </strong>
                        </div>
                      </div>
                    </li>)}
                </ul>}
            </div>

            <footer className="cart-drawer__footer" data-id="element-59">
              {checkoutError && <p className="cart-checkout-error" role="alert" data-id="element-60">
                  {checkoutError}
                </p>}
              <div className="cart-subtotal" data-id="element-61">
                <span data-id="element-62">Subtotal</span>
                <strong data-id="element-63">{formatCop(subtotal)}</strong>
              </div>
              <p className="cart-shipping-note" data-id="element-64">
                Envío calculado en el checkout.
              </p>
              <button type="button" className="cart-checkout" onClick={checkout} disabled={!items.length || isCheckingOut} data-id="element-65">
                {isCheckingOut ? 'Preparando checkout…' : 'Finalizar compra'}
              </button>
            </footer>
          </aside>
        </div>}
    </>;
}
function formatCop(value: number) {
  return `$${new Intl.NumberFormat('es-CO').format(value)} COP`;
}